import {gql} from "@apollo/client";

import {GetSLAReportFormRequirements} from "@/lib/queries/checks.ts";
import {CheckInfo} from "@/lib/queries/statuspages.ts";
import {AccountUser} from "@/lib/types/accounts.ts";
import {MonitoringServiceType, PerformanceDataType, ServiceTag} from "@/lib/types/checks.ts";

export enum StandardDateRange {
  LAST_24H = "LAST_24H",
  TODAY = "TODAY",
  YESTERDAY = "YESTERDAY",
  LAST_7D = "LAST_7D",
  LAST_30D = "LAST_30D",
  THIS_WEEK = "THIS_WEEK",
  LAST_WEEK = "LAST_WEEK",
  THIS_MONTH = "THIS_MONTH",
  LAST_MONTH = "LAST_MONTH",
  THIS_QUARTER = "THIS_QUARTER",
  LAST_QUARTER = "LAST_QUARTER",
  THIS_YEAR = "THIS_YEAR",
  LAST_YEAR = "LAST_YEAR",
  LAST_30_MINUTES = "LAST_30_MINUTES",
  LAST_1_HOUR = "LAST_1_HOUR",
  LAST_4_HOURS = "LAST_4_HOURS",
  LAST_12_HOURS = "LAST_12_HOURS",
  LAST_24_HOURS = "LAST_24_HOURS",
  LAST_7_DAYS = "LAST_7_DAYS",
  LAST_14_DAYS = "LAST_14_DAYS",
  LAST_30_DAYS = "LAST_30_DAYS",
  LAST_90_DAYS = "LAST_90_DAYS",
}

export enum SLAReportSort {
  BY_UPTIME = "BY_UPTIME",
  BY_SLA = "BY_SLA",
}

export enum SLAResponseTimeSectionSort {
  BY_RESPONSE_TIME = "BY_RESPONSE_TIME",
  BY_SLA = "BY_SLA",
}

export interface SLAReportGroupInput {
  name: string;
  group_services: number[];
}

/*
it is important for keep group_services name the same between SLAReportGroupInput and SLAReportGroup
due to API design and infinite scroll in the old UI group services manipulation requires extra carefulness
 */
export interface SLAReportGroup extends SLAReportGroupInput {
  id: number;
}

export interface SLAReport {
  id: number;
  name: string;
  created_at: Date;
  default_date_range: StandardDateRange;
  uptime_section_sort: SLAReportSort;
  response_time_section_sort: SLAResponseTimeSectionSort;
  show_uptime_section: boolean;
  show_uptime_sla: boolean;
  filter_with_downtime: boolean;
  filter_uptime_sla_violations: boolean;
  show_response_time_section: boolean;
  show_response_time_sla: boolean;
  filter_slowest: boolean;
  filter_response_time_sla_violations: boolean;
  key: string;
  tags: ServiceTag[];
  services_selected: CheckInfo[];
  groups: SLAReportGroup[];
  logo_url: string;
}

export interface SLAReportsResponse {
  slaReports: {
    total: number;
    items: SLAReport[];
  };
  tags: ServiceTag[];
}

export interface EditSLAReportResponse extends GetSLAReportFormRequirements {
  slaReport: SLAReport;
}

export const GQL_EDIT_SLA_REPORT = gql`
  query slaReport($id: Int!) {
    slaReport: slaReport(id: $id) {
      id
      name
      created_at
      default_date_range
      uptime_section_sort
      response_time_section_sort
      show_uptime_section
      show_uptime_sla
      filter_with_downtime
      filter_uptime_sla_violations
      show_response_time_section
      show_response_time_sla
      filter_slowest
      filter_response_time_sla_violations
      key
      logo_url
      tags {
        id
        tag
        color
        color_hex
      }
      services_selected {
        id
        name
        type
        address
      }
      groups {
        id
        name
        group_services
      }
    }
    monitoringServiceTypes {
      ...MonitoringGroupFields
    }
    tags {
      id
      tag
      color
      color_hex
    }
    slaReportGlobalLogo {
      logo_url
    }
  }
`;

export const GQL_SLA_REPORTS = gql`
  query slaReports($search: String, $filters: SLAReportFilters, $offset: Int, $limit: Int) {
    slaReports(search: $search, filters: $filters, offset: $offset, limit: $limit) {
      total
      items {
        id
        name
        key
        tags {
          id
          tag
          color
          color_hex
        }
        services_selected {
          id
          name
          type
          address
        }
        default_date_range
      }
    }
    tags {
      id
      tag
      color_hex
    }
  }
`;

export const GQL_DELETE_SLA_REPORT = gql`
  mutation DeleteSLAReport($id: Int!) {
    response: deleteSLAReport(id: $id) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_BULK_DELETE_SLA_REPORTS = gql`
  mutation BulkDeleteSLAReports($ids: [Int!]!) {
    response: bulkDeleteSLAReports(ids: $ids) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_SLA_REPORT = gql`
  mutation UpdateSLAReport($data: UpdateSLAReportInput!) {
    response: updateSLAReport(data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

interface SLAReportServiceTotals {
  downtime_secs: number;
  outages: number;
  response_time_avg?: number;
  response_time_max?: number;
  response_time_min?: number;
  uptime_pct?: number;
  uptime_sla?: number;
  response_time_sla_pct?: number;
}

// service entity copied from applogic.prepare_stats_for_services
export interface SLAReportService {
  service_id: number;
  service_name: string;
  device_name: string;
  uptime_sla: number;
  service_created_at: string;
  include_in_global_response_time: boolean;
  include_in_global_uptime: boolean;
  monitoring_service_type: MonitoringServiceType;
  performance_data_type: PerformanceDataType;
  cached_response_time: number;
  response_time_sla: number;
  totals: SLAReportServiceTotals;
}

export interface UptimeTotals {
  num_services: number;
  num_services_included_in_global_uptime: number;
  uptime_sla: number;
  uptime_pct: number;
  outages: number;
  downtime_secs: number;
  response_time_avg: number;
  response_time_max: number;
  response_time_min: number;
}

/* naming is very confusing due to a hierarchy and a non-normalized form of data serialized */
interface SLAReportServiceGroup {
  id: number;
  name: string;
}

export interface SLAReportViewGroup {
  group: SLAReportServiceGroup;
  uptime: {
    services: SLAReportService[];
  };
  uptime_totals: UptimeTotals;
}

export interface SLAReportResponseTime {
  name: string;
  response_time_avg?: number;
  response_time_sla?: number;
  response_time_sla_pct?: number;
  services: SLAReportService[];
  uptime_sla?: number;
}

export interface SLAReportViewResponse {
  slaReport: SLAReport;
  services: {
    items: SLAReportService[];
    uptime_totals: UptimeTotals;
    groups: SLAReportViewGroup[];
    response_time: SLAReportResponseTime[];
    uses_daily_response_time_data: boolean;
    hasGroups: boolean;
  };
}

export const GQL_SLA_REPORT_VIEW = gql`
  query SLAReportView($id: Int!, $query: String!, $start: DateTime!, $end: DateTime!, $skipGroup: Boolean!) {
    slaReport: slaReport(id: $id) {
      id
      name
      key
      created_at
      default_date_range
      uptime_section_sort
      response_time_section_sort
      show_uptime_section
      show_uptime_sla
      filter_with_downtime
      filter_uptime_sla_violations
      show_response_time_section
      show_response_time_sla
      filter_slowest
      filter_response_time_sla_violations
      logo_url
    }
    services: findSLAReportServices(input: {id: $id, query: $query, start: $start, end: $end, skipGroup: $skipGroup}) {
      response_time {
        name
        response_time_avg
        response_time_sla
        response_time_sla_pct
        uptime_sla
        services {
          service_id
          service_name
          device_name
          uptime_sla
          service_created_at
          include_in_global_response_time
          include_in_global_uptime
          monitoring_service_type
          performance_data_type
          cached_response_time
          response_time_sla
          totals {
            downtime_secs
            outages
            response_time_avg
            response_time_max
            response_time_min
            uptime_pct
            response_time_sla_pct
          }
        }
      }
      uses_daily_response_time_data
      items {
        service_id
        service_name
        device_name
        uptime_sla
        service_created_at
        include_in_global_response_time
        include_in_global_uptime
        monitoring_service_type
        performance_data_type
        cached_response_time
        response_time_sla
        totals {
          downtime_secs
          outages
          response_time_avg
          response_time_max
          response_time_min
          uptime_pct
        }
      }
      groups {
        group {
          id
          name
        }
        uptime {
          services {
            service_id
            service_name
            device_name
            uptime_sla
            totals {
              downtime_secs
              outages
              response_time_avg
              response_time_max
              response_time_min
              uptime_pct
              uptime_sla
            }
          }
        }
        uptime_totals {
          uptime_sla
          uptime_pct
          outages
          downtime_secs
          response_time_avg
        }
      }
      hasGroups
      uptime_totals {
        num_services
        num_services_included_in_global_uptime
        uptime_sla
        uptime_pct
        outages
      }
    }
  }
`;

export enum ScheduledReportRecurrence {
  DAILY = "DAILY",
  WEEKLY = "WEEKLY",
  MONTHLY = "MONTHLY",
  QUARTERLY = "QUARTERLY",
  YEARLY = "YEARLY",
}

export enum ScheduledReportFileType {
  PDF = "PDF",
  XLS = "XLS",
}

export interface ScheduledReport {
  id: number;
  name: string;
  created_at: Date;
  file_type: ScheduledReportFileType;
  recurrence: ScheduledReportRecurrence;
  default_date_range_override: StandardDateRange;
  on_weekday: number;
  on_weekday_display: string;
  at_time: number;
  at_time_display: string;
  schedule_display: string;
  is_enabled: boolean;
  recipient_emails: string[];
  recipient_users: number[];
  report?: SLAReport;
}

export interface ScheduleReportsResponse {
  scheduledReports: {
    items: ScheduledReport[];
    total: number;
  };
}

export const GQL_SCHEDULED_REPORTS = gql`
  query ScheduleReports($limit: Int!, $offset: Int!) {
    scheduledReports(limit: $limit, offset: $offset) {
      items {
        id
        name
        created_at
        file_type
        recurrence
        on_weekday
        default_date_range_override
        at_time
        at_time_display
        schedule_display
        is_enabled
        report {
          id
          name
        }
        recipient_emails
      }
      total
    }
  }
`;

export interface SLAReportsInfoResponse {
  slaReports: {
    items: SLAReport[];
  };
  currentAccountUsers: AccountUser[];
}

export const GQL_SLA_REPORTS_INFO = gql`
  query SLAReportsInfo($limit: Int!, $offset: Int!) {
    slaReports(offset: $offset, limit: $limit) {
      items {
        id
        name
        key
      }
    }
    currentAccountUsers {
      id
      first_name
      last_name
      email
    }
  }
`;

export const GQL_DELETE_SCHEDULED_REPORT = gql`
  mutation DeleteScheduledReport($id: Int!) {
    response: deleteScheduledReport(id: $id) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_BULK_DELETE_SCHEDULED_REPORTS = gql`
  mutation BulkDeleteScheduledReports($ids: [Int!]!) {
    response: bulkDeleteScheduledReports(ids: $ids) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UPDATE_SCHEDULED_REPORT = gql`
  mutation UpdateScheduledReport($data: UpdateScheduledReportInput!) {
    response: updateScheduledReport(data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface EditScheduledReportResponse extends SLAReportsInfoResponse {
  scheduledReport: ScheduledReport;
}

export const GQL_EDIT_SCHEDULED_REPORT = gql`
  query scheduledReport($id: Int!, $offset: Int!, $limit: Int!) {
    scheduledReport(id: $id) {
      id
      name
      file_type
      recurrence
      on_weekday
      default_date_range_override
      at_time
      is_enabled
      recipient_emails
      recipient_users
      report {
        id
        name
        key
      }
    }
    slaReports(offset: $offset, limit: $limit) {
      items {
        id
        name
        key
      }
    }
    currentAccountUsers {
      id
      first_name
      last_name
      email
    }
  }
`;

export interface SLAReportGlobalLogo {
  slaReportGlobalLogo: {
    logo_url: string;
  };
}

export const GQL_SLA_REPORT_GLOBAL_LOGO_QUERY = gql`
  query SLAReportGlobalLogo {
    slaReportGlobalLogo {
      logo_url
    }
  }
`;

export const GQL_SAVE_SLA_REPORT_GLOBAL_LOGO = gql`
  mutation SaveSLAReportGlobalLogo($logo: Upload) {
    response: saveSLAReportGlobalLogo(logo: $logo) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;
